/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `index` for the Caintra Connect frontend.
 * @module components/UserDetailsView/index
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { logger, sanitizeError } from "../../utils/logger.js";
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router";
import { getCompanyByRFC } from "../../logic/company/index.js";
import { updateUser } from "../../logic/user/index.js";
import styles from "./UserDetailsView.module.css";
import CircularProgress from "@mui/material/CircularProgress";
import Switch from "@mui/material/Switch";
import { SvgIcon, Backdrop } from "@mui/material";
import UserEditModal from "../../modals/UserEditModal.jsx";
import { useSelector } from "react-redux";
import LanguageSelector from "../LanguageSelector";
import NotificationBell from "../NotificationBell/index.jsx";
import ContactPageOutlinedIcon from "@mui/icons-material/ContactPageOutlined";
import { useTranslation } from "react-i18next";
import AssetImage from "../AssetImage";

import { ReactComponent as ActiveIcon } from "../../assets/icons/UserViews/ActiveIcon.svg";
import { ReactComponent as InactiveIcon } from "../../assets/icons/UserViews/InactiveIcon.svg";
import { ReactComponent as UserTitleBlackIcon } from "../../assets/icons/UserViews/UserTitleBlackIcon.svg";
import { ReactComponent as EmailBlackIcon } from "../../assets/icons/UserViews/EmailBlackIcon.svg";
import { ReactComponent as RFCBlackIcon } from "../../assets/icons/UserViews/RFCBlackIcon.svg";
import { ReactComponent as EditIcon } from "../../assets/icons/UserViews/EditIcon.svg";
import { ReactComponent as ChevronLeftIcon } from "../../assets/icons/UserViews/ChevronLeftIcon.svg";

/**
 * Full-page view that displays a user's details on mobile/tablet (name, member number,
 * company, status, title, email, phone, RFC) and provides edit and save actions. Can open
 * UserEditModal or call onEditClick for tablet/desktop. Mirrors UserDisplayModal behavior.
 * Used when the app is in mobile/tablet layout and the user selects a user to view.
 *
 * @param {Object} props
 * @param {Object} props.user - The user to display
 * @param {function} props.onBack - Called when the user navigates back
 * @param {function} props.onUpdateUser - Called when the user is updated (e.g. after edit or save)
 * @param {Object} [props.currentUser] - The currently logged-in user; used to show/hide edit and save by role
 * @param {function} [props.onEditClick] - If provided (e.g. tablet), called with user when edit is clicked; otherwise the view opens UserEditModal
 */
const UserDetailsView = ({
  user,
  onBack,
  onUpdateUser,
  currentUser,
  onEditClick,
}) => {
  const navigate = useNavigate();
  const profilePic = useSelector(() => user.imagenDePerfil);
  const { t } = useTranslation();

  const [tempImage, setTempImage] = useState(null);
  const [imageKey, setImageKey] = useState(Date.now());
  const [activo, setIsActive] = useState(false);
  const [unsubscribedFromCustomEmails, setUnsubscribedFromCustomEmails] =
    useState(false);
  const [empresa, setCompany] = useState("");
  const [imagenDePerfil, setProfileImage] = useState("");
  const [imagenDePerfilFormato, setProfileImageFormat] = useState("");
  const [numeroSocio, setMemberNumber] = useState("");
  const [tipoDeUsuario, setUserType] = useState("");
  const [nombre, setName] = useState("");
  const [titulo, setTitle] = useState("");
  const [correoElectronico, setEmail] = useState("");
  const [numeroCelular, setNumeroCelular] = useState("");
  const [RFC, setRFC] = useState("");
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isLoadingCompany, setIsLoadingCompany] = useState(false);

  /* --- Update (delegate to logic) --- */

  /**
   * Sends updated user data (activo, imagenDePerfil) to the API. On success calls onUpdateUser and onBack.
   */
  const handleUpdateUser = async () => {
    if (!user._id) {
      logger.error("User ID is missing");
      return;
    }

    const updatedUserData = {
      ...user,
      activo,
      unsubscribedFromCustomEmails,
      unsubscribedFromCustomEmailsAt: unsubscribedFromCustomEmails
        ? user.unsubscribedFromCustomEmailsAt || new Date().toISOString()
        : null,
      unsubscribedFromCustomEmailsSource: unsubscribedFromCustomEmails
        ? user.unsubscribedFromCustomEmailsSource || "admin"
        : null,
      imagenDePerfil,
      imagenDePerfilFormato,
    };

    setLoading(true);
    try {
      await updateUser(user._id, updatedUserData, {
        navigate,
        onSuccess: (updatedUser) => {
          onUpdateUser(updatedUser);
          setImageKey(Date.now());
          onBack();
        },
        onError: (errorData) => {
          logger.error("Error updating user:", { data: errorData });
        },
      });
    } catch (error) {
      logger.error("Error:", { err: sanitizeError(error) });
    } finally {
      setLoading(false);
    }
  };

  /* --- Effects (sync state and company data) --- */

  /**
   * Syncs local state with the displayed user when the view mounts/user changes.
   * Resets switch edits if the user navigates away without saving.
   */
  useEffect(() => {
    if (user) {
      setIsActive(user.activo);
      setUnsubscribedFromCustomEmails(
        user.unsubscribedFromCustomEmails === true,
      );
      setProfileImage(user.imagenDePerfil);
      setProfileImageFormat(user.imagenDePerfilFormato);
      setUserType(user.nombreRol);
      setName(user.nombre);
      setTitle(user.titulo);
      setEmail(user.correoElectronico);
      setNumeroCelular(user.numeroCelular ?? "");
      setRFC(user.RFC);
      setImageKey(Date.now());
      setCompany("");
      setMemberNumber("");
      setIsLoadingCompany(false);
    }
  }, [user, user?._id, user?.activo, user?.unsubscribedFromCustomEmails]);

  /**
   * Fetches company data by user RFC when user.RFC is available and updates company name and member number.
   * Uses getCompanyByRFC; clears company when user has no RFC.
   */
  useEffect(() => {
    const fetchCompanyData = async () => {
      if (user?.RFC && !empresa) {
        setIsLoadingCompany(true);
        try {
          const companyData = await getCompanyByRFC(user.RFC, navigate);
          if (companyData) {
            setCompany(companyData.nombreComercial);
            setMemberNumber(companyData.numeroSocio ?? "");
          }
        } catch (error) {
          logger.error("Error fetching company data:", {
            err: sanitizeError(error),
          });
        } finally {
          setIsLoadingCompany(false);
        }
      }
    };

    fetchCompanyData();
  }, [user?.RFC, navigate]);

  /* --- Action handlers --- */

  /**
   * Toggles the active/inactive state for the displayed user (local state; persisted on save).
   */
  const handleToggleActive = () => {
    setIsActive((prevActive) => !prevActive);
  };

  const handleToggleCustomEmailSubscription = () => {
    setUnsubscribedFromCustomEmails((prev) => !prev);
  };

  /**
   * Handles edit action: calls onEditClick(user) if provided (tablet), otherwise opens UserEditModal.
   * Resets tempImage when opening the modal.
   */
  const handleEditClick = () => {
    if (onEditClick) {
      // If onEditClick is provided, use it (for tablet view)
      onEditClick(user);
    } else {
      // Otherwise, open the modal (for desktop view)
      setEditModalOpen(true);
      setTempImage(null);
    }
  };

  /**
   * Called when the user is updated from the edit modal. Passes the updated user to the parent and closes the edit modal.
   * @param {Object} updatedUser - The user object returned from the update API.
   * Closes the edit modal and passes updated user to parent.
   */
  const handleUpdateUserFromEditModal = (updatedUser) => {
    onUpdateUser(updatedUser);
    setEditModalOpen(false);
  };

  return (
    <>
      <div className={styles.userDetailsContainer}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.languages}>
            <NotificationBell navigate={navigate} />
            <LanguageSelector />
          </div>

          {/* Back navigation */}
          <div className={styles.backNavigation}>
            <button className={styles.backButton} onClick={onBack}>
              <ChevronLeftIcon />
              <span>{t("common.goBack")}</span>
            </button>
          </div>

          {/* Page title */}
          <div className={styles.headerInfoContainer}>
            <div className={styles.headerInfo}>
              <SvgIcon sx={{ fontSize: "32px" }} viewBox="0 0 24 24">
                <defs>
                  <linearGradient
                    id="gradient1"
                    x1="0%"
                    y1="0%"
                    x2="100%"
                    y2="100%"
                  >
                    <stop offset="0%" stopColor="#4eabe7" />
                    <stop offset="100%" stopColor="#2f59df" />
                  </linearGradient>
                </defs>
                <ContactPageOutlinedIcon sx={{ fill: "url(#gradient1)" }} />
              </SvgIcon>
              <h1 className={styles.title}>
                {t("components.userDetailsView.header")}
              </h1>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className={styles.content}>
          {/* Avatar Section */}
          <div className={styles.avatarSection}>
            <div className={styles.avatarContainer}>
              <div className={styles.avatar}>
                {imagenDePerfil ? (
                  <AssetImage
                    src={tempImage || profilePic}
                    alt={t("common.avatarAlt")}
                    className={styles.avatarImage}
                  />
                ) : nombre ? (
                  nombre.charAt(0)
                ) : (
                  ""
                )}
              </div>
            </div>
          </div>

          {/* User Info */}
          <div className={styles.userInfo}>
            <h1 className={styles.userName}>{nombre}</h1>

            {numeroSocio && (
              <div className={styles.memberNumber}>
                {t("components.userDetailsView.memberNumber")}: {numeroSocio}
              </div>
            )}

            <div className={styles.company}>
              {empresa || t("common.companyNotFound")}
            </div>

            <div className={styles.activeStatus}>
              {activo ? (
                <span className={styles.activeIcon}>
                  <ActiveIcon />
                </span>
              ) : (
                <span className={styles.inactiveIcon}>
                  <InactiveIcon />
                </span>
              )}
              <span className={styles.statusText}>
                {activo
                  ? t("components.userDetailsView.active")
                  : t("components.userDetailsView.inactive")}
              </span>
              {currentUser?.tipoDeUsuario !== "3" &&
                currentUser?.tipoDeUsuario !== "4" &&
                currentUser?.tipoDeUsuario !== "12" && (
                  <Switch
                    checked={activo}
                    onChange={handleToggleActive}
                    color="primary"
                  />
                )}
            </div>

            <div className={styles.activeStatus}>
              <span
                className={
                  unsubscribedFromCustomEmails
                    ? styles.inactiveIcon
                    : styles.activeIcon
                }
              >
                {unsubscribedFromCustomEmails ? (
                  <InactiveIcon />
                ) : (
                  <ActiveIcon />
                )}
              </span>
              <span className={styles.statusText}>
                {unsubscribedFromCustomEmails
                  ? t("components.userDetailsView.unsubscribedFromCustomEmails")
                  : t("components.userDetailsView.subscribedToCustomEmails")}
              </span>
              {currentUser?.tipoDeUsuario === "5" && (
                <Switch
                  checked={!unsubscribedFromCustomEmails}
                  onChange={handleToggleCustomEmailSubscription}
                  color="primary"
                />
              )}
            </div>

            <hr className={styles.divider} />

            {/* Title */}
            <div className={styles.infoSection}>
              <div className={styles.infoIcon}>
                <UserTitleBlackIcon />
              </div>
              <div className={styles.infoContent}>
                <h5 className={styles.infoLabel}>
                  {t("components.userDetailsView.title")}:
                </h5>
                <h2 className={styles.infoValue}>{titulo}</h2>
              </div>
            </div>

            {/* Email */}
            <div className={styles.infoSection}>
              <div className={styles.infoIcon}>
                <EmailBlackIcon />
              </div>
              <div className={styles.infoContent}>
                <h5 className={styles.infoLabel}>
                  {t("components.userDetailsView.email")}:
                </h5>
                <h2 className={styles.infoValue}>{correoElectronico}</h2>
              </div>
            </div>

            {/* Phone */}
            <div className={styles.infoSection}>
              <div className={styles.infoIcon}>
                <span style={{ width: 24, display: "inline-block" }} />
              </div>
              <div className={styles.infoContent}>
                <h5 className={styles.infoLabel}>
                  {t("components.userDetailsView.phone")}:
                </h5>
                <h2 className={styles.infoValue}>{numeroCelular || "—"}</h2>
              </div>
            </div>

            {/* RFC */}
            <div className={styles.infoSection}>
              <div className={styles.infoIcon}>
                <RFCBlackIcon />
              </div>
              <div className={styles.infoContent}>
                <h5 className={styles.infoLabel}>
                  {t("components.userDetailsView.rfc")}:
                </h5>
                <h2 className={styles.infoValue}>{RFC}</h2>
              </div>
            </div>

            {/* Action Buttons */}
            {currentUser?.tipoDeUsuario !== "3" &&
              currentUser?.tipoDeUsuario !== "4" &&
              currentUser?.tipoDeUsuario !== "12" && (
                <div className={styles.actionButtons}>
                  <button
                    className={styles.editButton}
                    onClick={handleEditClick}
                  >
                    <EditIcon />
                    {t("components.userDetailsView.editInformation")}
                  </button>
                </div>
              )}

            {currentUser?.tipoDeUsuario !== "3" &&
              currentUser?.tipoDeUsuario !== "4" &&
              currentUser?.tipoDeUsuario !== "12" && (
                <div className={styles.saveButtons}>
                  <button onClick={onBack} className={styles.cancelButton}>
                    {t("components.userDetailsView.cancel")}
                  </button>
                  <button
                    className={styles.saveButton}
                    onClick={handleUpdateUser}
                    disabled={loading}
                  >
                    {loading ? (
                      <CircularProgress size={24} />
                    ) : (
                      t("components.userDetailsView.save")
                    )}
                  </button>
                </div>
              )}
          </div>
        </div>
      </div>

      {/* User Edit Modal */}
      {user && (
        <UserEditModal
          open={editModalOpen}
          onClose={() => setEditModalOpen(false)}
          user={user}
          onUpdateUser={handleUpdateUserFromEditModal}
        />
      )}

      <Backdrop
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          backdropFilter: "blur(4px)",
        }}
        open={isLoadingCompany}
      >
        <CircularProgress
          size={60}
          thickness={4}
          sx={{
            color: "#4eabe7",
          }}
        />
      </Backdrop>
    </>
  );
};

export default UserDetailsView;
